import * as React from 'react';
import { Form } from 'react-bootstrap';
import { EventRepetition } from 'cb-model';
import { EventDetails } from './eventDetailsFormTypes';

const WEEKDAYS = [
  { id: 0, label: 'Sun' },
  { id: 1, label: 'Mon' },
  { id: 2, label: 'Tue' },
  { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' },
  { id: 5, label: 'Fri' },
  { id: 6, label: 'Sat' },
];

const getDefaultWeekday = (startDate?: string) => {
  const parsed = startDate ? new Date(startDate) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().getDay() : parsed.getDay();
};

type Props = {
  value: EventDetails;
  isUpdate?: boolean;
  onChange: (repetition: EventRepetition | undefined) => void;
};

export const EventRepetitionSection = ({ value, isUpdate, onChange }: Props): React.JSX.Element => {
  const repetition = value.repetition;
  const isExistingRepetition = !!isUpdate && !!repetition;

  const updateRepetition = (updater: (current: EventRepetition) => EventRepetition) => {
    if (!repetition || isExistingRepetition) return;
    onChange(updater(repetition));
  };

  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Repeat event</div>
      <Form.Text muted className="d-block mb-2">
        Create separate events on a weekly schedule. Each event can be managed independently.
      </Form.Text>
      <Form.Check
        type="switch"
        id="event-repetition-enabled"
        label="Repeat this event"
        checked={!!repetition}
        disabled={isExistingRepetition}
        onChange={(event) => {
          if (isExistingRepetition) return;
          onChange(
            event.currentTarget.checked
              ? {
                  frequency: 'weekly',
                  interval_weeks: 1,
                  weekdays: [getDefaultWeekday(value.start_date)],
                  end_date: '',
                }
              : undefined,
          );
        }}
      />

      {isExistingRepetition && (
        <Form.Text muted>
          This repeated-event set cannot yet be changed. You can still update this individual event.
        </Form.Text>
      )}

      {repetition && (
        <div className="mt-3">
          <Form.Group className="mb-3" controlId="event-repeat-interval">
            <Form.Label>Repeat every</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={repetition.interval_weeks}
                disabled={isExistingRepetition}
                onChange={(event) =>
                  updateRepetition((current) => ({
                    ...current,
                    interval_weeks: Number(event.currentTarget.value),
                  }))
                }
                style={{ maxWidth: 90 }}
              />
              <span>week(s)</span>
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="event-repeat-weekdays">
            <Form.Label>Repeat on</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {WEEKDAYS.map((weekday) => (
                <Form.Check
                  key={weekday.id}
                  inline
                  type="checkbox"
                  id={`event-repeat-weekday-${weekday.id}`}
                  label={weekday.label}
                  checked={repetition.weekdays.includes(weekday.id)}
                  disabled={isExistingRepetition}
                  onChange={(event) =>
                    updateRepetition((current) => ({
                      ...current,
                      weekdays: event.currentTarget.checked
                        ? [...current.weekdays, weekday.id].sort((a, b) => a - b)
                        : current.weekdays.filter((value) => value !== weekday.id),
                    }))
                  }
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group controlId="event-repeat-end-date">
            <Form.Label>Repeat until*</Form.Label>
            <Form.Control
              type="date"
              min={value.start_date ? new Date(value.start_date).toISOString().substring(0, 10) : undefined}
              value={repetition.end_date}
              disabled={isExistingRepetition}
              onChange={(event) =>
                updateRepetition((current) => ({
                  ...current,
                  end_date: event.currentTarget.value,
                }))
              }
            />
            <Form.Text muted>The final event may be on this date.</Form.Text>
          </Form.Group>
        </div>
      )}
    </div>
  );
};
